import React, { createContext, useContext, useState, useRef, useEffect, useMemo } from 'react';
import { recommendationsAPI, watchlistAPI, isApiPaused } from '../services/api';
import { useAuth } from './AuthContext';
import { useWebSocket } from './WebSocketContext';
import toast from 'react-hot-toast';

const StreamingContext = createContext();

export const StreamingProvider = ({ children }) => {
  const { user } = useAuth();
  const { connected: wsConnected, fallbackActive, subscribe: wsSubscribe, unsubscribe: wsUnsubscribe } = useWebSocket();
  const [loading, setLoading] = useState(true);
  const [watchlists, setWatchlists] = useState([]);
  const [selectedWatchlist, setSelectedWatchlist] = useState(null);
  const [streamingData, setStreamingData] = useState({});
  const [connectionLogs, setConnectionLogs] = useState([]);
  const [subscribedInstruments, setSubscribedInstruments] = useState([]);
  const [lastDataUpdate, setLastDataUpdate] = useState(null);
  const [dataSaveCount, setDataSaveCount] = useState(0);
  const [tickHistory, setTickHistory] = useState({});
  const [tickWindow, setTickWindow] = useState(30);
  const [saveInterval, setSaveInterval] = useState(60); // 60 s default (was 30 — too aggressive)
  const [persistedBatches, setPersistedBatches] = useState([]);
  const persistIntervalRef = useRef(null);
  const wsRef = useRef(null);

  // Use WebSocket connection status or fallback status
  const connected = wsConnected || fallbackActive;
  // Add log entry
  const addLog = (message, type = 'info') => {
    const logEntry = {
      id: Date.now() + Math.random(),
      message,
      type,
      timestamp: new Date()
    };
    setConnectionLogs(prev => [logEntry, ...prev.slice(0, 99)]);
  };

  useEffect(() => {
    if (user?.kiteConnected) {
      addLog('🚀 Streaming initialized');
      fetchWatchlists();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [user]);



  const handleTickData = (tickData) => {
    if (!tickData || !tickData.instrumentToken) return;
    setStreamingData(prev => ({
      ...prev,
      [tickData.instrumentToken]: {
        ...tickData,
        lastUpdate: new Date()
      }
    }));
    setTickHistory(prev => {
      const prevArr = prev[tickData.instrumentToken] || [];
      const newArr = [...prevArr, tickData.ltp];
      if (newArr.length > 60) newArr.shift();
      return { ...prev, [tickData.instrumentToken]: newArr };
    });
    setLastDataUpdate(new Date());
    addLog(`📈 ${tickData.source === 'fallback' ? 'API' : 'WS'}: ${tickData.symbol} @ ₹${tickData.ltp}`);
  };

  // Listen to WebSocket context for live data updates
  useEffect(() => {
    const handleWebSocketData = (event) => {
      if (event.detail && event.detail.type === 'tick') {
        handleTickData(event.detail.data);
      }
    };
    
    window.addEventListener('websocketData', handleWebSocketData);
    return () => window.removeEventListener('websocketData', handleWebSocketData);
  }, []);
  const fetchWatchlists = async () => {
    try {
      addLog('📋 Fetching watchlists...');
      const response = await watchlistAPI.getWatchlists();
      const watchlistData = response.data;
      setWatchlists(watchlistData);
      if (watchlistData.length > 0) {
        setSelectedWatchlist(watchlistData[0]);
        addLog(`✅ Found ${watchlistData.length} watchlists`, 'success');
      } else {
        addLog('⚠️ No watchlists found', 'warning');
      }
    } catch (error) {
      addLog(`❌ Failed to fetch watchlists: ${error.message}`, 'error');
      toast.error('Failed to fetch watchlists');
    } finally {
      setLoading(false);
    }
  };

  const createNifty500Watchlist = async () => {
    try {
      addLog('🏗️ Creating NIFTY 500 watchlist...');
      const response = await watchlistAPI.createNifty500Watchlist();
      addLog(`✅ Created NIFTY 500 watchlist with ${response.data.instrumentCount} stocks`, 'success');
      toast.success(`NIFTY 500 watchlist created with ${response.data.instrumentCount} stocks`);
      await fetchWatchlists();
    } catch (error) {
      addLog(`❌ Failed to create NIFTY 500: ${error.message}`, 'error');
      toast.error('Failed to create NIFTY 500 watchlist');
    }
  };

  const startStreaming = () => {
    if (!selectedWatchlist || !selectedWatchlist.instruments.length) {
      toast.error('No instruments to stream');
      return;
    }
    const instrumentTokens = selectedWatchlist.instruments.map(inst => inst.instrumentToken);
    const instruments = selectedWatchlist.instruments.map(inst => `${inst.exchange}:${inst.symbol}`);
    addLog(`🚀 Starting streaming for ${instrumentTokens.length} instruments`);
    
    // Use WebSocket context subscribe method (includes fallback)
    wsSubscribe(instruments);
    setSubscribedInstruments(instrumentTokens);
    addLog(`📡 Subscription request sent for ${instrumentTokens.length} instruments`, 'success');
    
    if (!wsConnected && fallbackActive) {
      addLog('📡 Using API fallback for live data', 'info');
    }
  };

  const stopStreaming = () => {
    if (subscribedInstruments.length === 0) return;
    addLog('🛑 Stopping streaming...');
    
    // Use WebSocket context unsubscribe method
    wsUnsubscribe(subscribedInstruments);
    setSubscribedInstruments([]);
    setStreamingData({});
    addLog('✅ Streaming stopped', 'success');
  };

  // Recommendations logic
  function stddev(arr) {
    if (!arr || arr.length < 2) return 0;
    const mean = arr.reduce((a, b) => a + b, 0) / arr.length;
    const variance = arr.reduce((a, b) => a + (b - mean) ** 2, 0) / (arr.length - 1);
    return Math.sqrt(variance);
  }

  const getRecommendations = () => {
    if (!selectedWatchlist) return { growth: [], fall: [], sinceOpen: [], lastN: [], losersOpen: [], losersN: [] };
    const growthCandidates = [];
    const fallCandidates = [];
    const sinceOpen = [];
    const lastN = [];
    const N = tickWindow;

    for (const inst of selectedWatchlist.instruments) {
      // Validate instrument data
      if (!inst || !inst.symbol || inst.symbol === 'N/A' || inst.symbol === 'undefined' || inst.symbol.trim() === '') {
        continue; // Skip invalid instruments
      }
      
      const tick = streamingData[inst.instrumentToken];
      const hist = tickHistory[inst.instrumentToken];
      if (!tick || !tick.close) continue;

      // Since close
      const pctChangeSinceClose = ((tick.ltp - tick.close) / tick.close) * 100;
      sinceOpen.push({ symbol: inst.symbol, pctChange: pctChangeSinceClose });

      // Last N ticks
      if (hist && hist.length >= N && hist[0] > 0) {
        const pctN = ((hist[hist.length - 1] - hist[0]) / hist[0]) * 100;
        lastN.push({ symbol: inst.symbol, pctChange: pctN });
      }

      // --- Linear regression for robust trend detection ---
      if (!hist || hist.length < N * 2) continue;
      const recent = hist.slice(-N);
      const prev = hist.slice(-N * 2, -N);
      const n = recent.length;
      const x = Array.from({ length: n }, (_, i) => i);
      const y = recent;
      const xMean = x.reduce((a, b) => a + b, 0) / n;
      const yMean = y.reduce((a, b) => a + b, 0) / n;
      const b = x.reduce((sum, xi, i) => sum + (xi - xMean) * (y[i] - yMean), 0) /
                x.reduce((sum, xi) => sum + (xi - xMean) ** 2, 0);
      const a = yMean - b * xMean;
      // R-squared
      const ssTot = y.reduce((sum, yi) => sum + (yi - yMean) ** 2, 0);
      const ssRes = y.reduce((sum, yi, i) => sum + (yi - (a + b * x[i])) ** 2, 0);
      const r2 = 1 - ssRes / ssTot;
      // Volatility
      const volatility = Math.sqrt(y.reduce((sum, yi) => sum + (yi - yMean) ** 2, 0) / n);
      // pctChange for this window
      const pctChange = ((recent[n - 1] - recent[0]) / recent[0]) * 100;
      // Previous window slope for momentum
      let momentum = null;
      if (prev.length === N) {
        const prevX = x;
        const prevY = prev;
        const prevXMean = prevX.reduce((a, b) => a + b, 0) / n;
        const prevYMean = prevY.reduce((a, b) => a + b, 0) / n;
        const prevB = prevX.reduce((sum, xi, i) => sum + (xi - prevXMean) * (prevY[i] - prevYMean), 0) /
                      prevX.reduce((sum, xi) => sum + (xi - prevXMean) ** 2, 0);
        momentum = b - prevB;
      }
      // Only consider if trend is strong and consistent
      if (r2 < 0.7) continue;
      
      // Validate the recommendation object before adding
      const recObj = {
        symbol: inst.symbol,
        ltp: recent[recent.length - 1],
        close: tick.close,
        slope: b,
        r2,
        volatility,
        pctChange,
        momentum,
        score: b // use slope as score
      };
      
      // Additional validation to ensure we have valid data
      if (recObj.ltp && recObj.ltp > 0 && recObj.close && recObj.close > 0) {
        if (b > 0) {
          growthCandidates.push(recObj);
        }
        if (b < 0) {
          fallCandidates.push(recObj);
        }
      }
    }

    // Sort by slope (trend strength)
    growthCandidates.sort((a, b) => b.slope - a.slope);
    fallCandidates.sort((a, b) => a.slope - b.slope);

    // Add rank, pctChange, momentum to each recommendation
    const addFields = (arr, type) =>
      arr.slice(0, 2).map((rec, idx) => ({
        symbol: rec.symbol,
        ltp: rec.ltp,
        close: rec.close,
        pctChange: rec.pctChange !== undefined ? rec.pctChange : null,
        momentum: rec.momentum !== undefined ? rec.momentum : null,
        volatility: rec.volatility,
        score: rec.slope // Use slope as score for backend compatibility
      }));

    // Top/bottom 10 for sinceOpen/lastN/losersOpen/losersN remain unchanged
    sinceOpen.sort((a, b) => b.pctChange - a.pctChange);
    lastN.sort((a, b) => b.pctChange - a.pctChange);
    const losersOpen = [...sinceOpen].sort((a, b) => a.pctChange - b.pctChange).slice(0, 10);
    const losersN = [...lastN].sort((a, b) => a.pctChange - b.pctChange).slice(0, 10);

    return {
      growth: addFields(growthCandidates, 'growth'),
      fall: addFields(fallCandidates, 'fall'),
      sinceOpen: sinceOpen.slice(0, 10),
      lastN: lastN.slice(0, 10),
      losersOpen,
      losersN
    };
  };
  const recommendations = useMemo(getRecommendations, [selectedWatchlist, streamingData, tickHistory, tickWindow]);

  // DB persistence
  const recommendationsRef = useRef({ growth: [], fall: [] });
  useEffect(() => {
    recommendationsRef.current = recommendations;
  }, [recommendations]);

  const isStreaming = subscribedInstruments.length > 0;
  useEffect(() => {
    if (!isStreaming || !selectedWatchlist) return;
    if (persistIntervalRef.current) clearInterval(persistIntervalRef.current);
    persistIntervalRef.current = setInterval(() => {
      if (isApiPaused()) return  // back off during rate-limit pause
      const { growth, fall } = recommendationsRef.current;
      if ((growth.length === 0 && fall.length === 0)) return;
      persistRecommendations(growth, fall);
    }, saveInterval * 1000);
    const { growth, fall } = recommendationsRef.current;
    if (growth.length > 0 || fall.length > 0) {
      persistRecommendations(growth, fall);
    }
    return () => clearInterval(persistIntervalRef.current);
  }, [isStreaming, tickWindow, selectedWatchlist, saveInterval]);

  const persistRecommendations = async (growth, fall) => {
    if (!selectedWatchlist) return;
    if (!selectedWatchlist._id || !tickWindow) return;
    if ((growth.length === 0 && fall.length === 0)) return;
    
    // Filter out invalid recommendations before saving
    const validGrowth = growth.filter(rec => 
      rec && 
      rec.symbol && 
      rec.symbol !== 'N/A' && 
      rec.symbol !== 'undefined' && 
      rec.symbol.trim() !== '' &&
      rec.ltp && 
      rec.ltp > 0 &&
      rec.close && 
      rec.close > 0
    );
    
    const validFall = fall.filter(rec => 
      rec && 
      rec.symbol && 
      rec.symbol !== 'N/A' && 
      rec.symbol !== 'undefined' && 
      rec.symbol.trim() !== '' &&
      rec.ltp && 
      rec.ltp > 0 &&
      rec.close && 
      rec.close > 0
    );
    
    if (validGrowth.length === 0 && validFall.length === 0) {
      addLog('⚠️ No valid recommendations to persist', 'warning');
      return;
    }
    
    console.log('=== FRONTEND: persistRecommendations called ===');
    console.log('Selected watchlist:', selectedWatchlist._id);
    console.log('Tick window:', tickWindow);
    console.log('Valid growth recommendations:', validGrowth);
    console.log('Valid fall recommendations:', validFall);
    
    try {
      const payload = {
        watchlistId: selectedWatchlist._id,
        tickWindow,
        growth: validGrowth,
        fall: validFall,
        timestamp: new Date().toISOString()
      };
      addLog(`Saving ${validGrowth.length} growth and ${validFall.length} fall recommendations to DB...`, 'info');
      addLog(`Payload: ${JSON.stringify(payload, null, 2)}`);
      console.log('=== FRONTEND: Sending streaming recommendations ===');
      console.log('Payload:', payload);
      console.log('Growth count:', validGrowth.length);
      console.log('Fall count:', validFall.length);
      console.log('Growth sample:', validGrowth[0]);
      console.log('Fall sample:', validFall[0]);
      
      const res = await recommendationsAPI.saveStreamingMinuteV2(payload);
      console.log('=== FRONTEND: Response received ===');
      console.log('Response:', res);
      console.log('Response data:', res.data);
      
      setPersistedBatches([]);
      addLog('💾 Recommendations persisted to DB (v2)', 'success');
      toast.success('Recommendations saved to DB');
      addLog(`DB Response: ${JSON.stringify(res.data)}`);
      
      // Trigger Gemini analysis with the latest recommendations
      if (validGrowth.length > 0 || validFall.length > 0) {
        addLog('🤖 Triggering Gemini analysis with latest recommendations...', 'info');
        try {
          // Emit a custom event to notify the IntradayRobust component
          const event = new CustomEvent('streamingRecommendationsUpdated', {
            detail: { growth: validGrowth, fall: validFall }
          });
          window.dispatchEvent(event);
          addLog('📡 Notified components about new recommendations', 'success');
        } catch (error) {
          addLog(`❌ Failed to trigger Gemini analysis: ${error.message}`, 'error');
        }
      }
    } catch (err) {
      console.error('=== FRONTEND: Error saving recommendations ===');
      console.error('Error:', err);
      console.error('Error response:', err.response);
      addLog('❌ Failed to persist recommendations: ' + (err.message || 'Unknown error'), 'error');
      toast.error('Failed to save recommendations to DB');
    }
  };

  return (
    <StreamingContext.Provider value={{
      loading,
      watchlists,
      selectedWatchlist,
      setSelectedWatchlist,
      streamingData,
      connectionLogs,
      connected,
      subscribedInstruments,
      lastDataUpdate,
      dataSaveCount,
      tickHistory,
      tickWindow,
      setTickWindow,
      saveInterval,
      setSaveInterval,
      persistedBatches,
      isStreaming,
      recommendations,
      fetchWatchlists,
      createNifty500Watchlist,
      startStreaming,
      stopStreaming,
      addLog
    }}>
      {children}
    </StreamingContext.Provider>
  );
};

export const useStreaming = () => useContext(StreamingContext); 